import {formatNumber} from "@/adapters/push.adapter";
import {BaseBadge} from "@/components/BaseBadge";
import type {AudienceFilters, AudienceType, CampaignCategory, CampaignPriority, DataPayload} from "@/types/push.types";
import {CheckCircle} from "lucide-react";

interface Step3ReviewProps {
  formData: {
    title: string;
    body: string;
    category: CampaignCategory;
    priority: CampaignPriority;
    imageUrl?: string;
    actionUrl?: string;
    dataPayload?: DataPayload;
    audienceType: AudienceType;
    audienceFilters?: AudienceFilters;
    targetUserId?: string;
  };
  estimatedRecipients: number;
}

export const Step3Review = ({formData, estimatedRecipients}: Step3ReviewProps) => {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2
          className="text-lg font-semibold mb-1"
          style={{color: "var(--color-text)"}}
        >
          Step 3: Review & Send
        </h2>
        <p
          className="text-sm"
          style={{color: "var(--color-text-secondary)"}}
        >
          Review your campaign before creating
        </p>
      </div>

      {/* Content Summary */}
      <div
        className="p-4 rounded-lg"
        style={{backgroundColor: "var(--color-surface-2)"}}
      >
        <h3
          className="text-sm font-semibold mb-3"
          style={{color: "var(--color-text)"}}
        >
          Content
        </h3>
        <div className="flex flex-col gap-2 text-sm">
          <div className="flex justify-between">
            <span style={{color: "var(--color-text-secondary)"}}>Title:</span>
            <span
              style={{color: "var(--color-text)"}}
              className="font-medium"
            >
              {formData.title}
            </span>
          </div>
          <div className="flex justify-between">
            <span style={{color: "var(--color-text-secondary)"}}>Body:</span>
            <span
              style={{color: "var(--color-text)"}}
              className="max-w-md text-right"
            >
              {formData.body}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span style={{color: "var(--color-text-secondary)"}}>Category:</span>
            <BaseBadge
              label={formData.category}
              variant="neutral"
            />
          </div>
          <div className="flex justify-between items-center">
            <span style={{color: "var(--color-text-secondary)"}}>Priority:</span>
            <BaseBadge
              label={formData.priority}
              variant={formData.priority === "high" ? "warning" : "neutral"}
            />
          </div>
          {formData.imageUrl && (
            <div className="flex justify-between">
              <span style={{color: "var(--color-text-secondary)"}}>Image:</span>
              <span
                style={{color: "var(--color-text)"}}
                className="text-xs truncate max-w-xs"
              >
                {formData.imageUrl}
              </span>
            </div>
          )}
          {formData.dataPayload?.screen && (
            <div className="flex justify-between">
              <span style={{color: "var(--color-text-secondary)"}}>Navigation:</span>
              <span style={{color: "var(--color-text)"}}>{formData.dataPayload.screen}</span>
            </div>
          )}
          {formData.actionUrl && (
            <div className="flex justify-between">
              <span style={{color: "var(--color-text-secondary)"}}>Action URL:</span>
              <span
                style={{color: "var(--color-text)"}}
                className="text-xs truncate max-w-xs"
              >
                {formData.actionUrl}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Audience Summary */}
      <div
        className="p-4 rounded-lg"
        style={{backgroundColor: "var(--color-surface-2)"}}
      >
        <h3
          className="text-sm font-semibold mb-3"
          style={{color: "var(--color-text)"}}
        >
          Audience
        </h3>
        <div className="flex flex-col gap-2 text-sm">
          <div className="flex justify-between">
            <span style={{color: "var(--color-text-secondary)"}}>Type:</span>
            <span
              style={{color: "var(--color-text)"}}
              className="capitalize font-medium"
            >
              {formData.audienceType}
            </span>
          </div>

          {formData.audienceType === "individual" && (
            <div className="flex justify-between">
              <span style={{color: "var(--color-text-secondary)"}}>User ID:</span>
              <span
                style={{color: "var(--color-text)"}}
                className="text-xs font-mono"
              >
                {formData.targetUserId}
              </span>
            </div>
          )}

          {formData.audienceType === "segment" && formData.audienceFilters && (
            <>
              {formData.audienceFilters.countries && (
                <div className="flex justify-between">
                  <span style={{color: "var(--color-text-secondary)"}}>Countries:</span>
                  <span style={{color: "var(--color-text)"}}>{formData.audienceFilters.countries.join(", ")}</span>
                </div>
              )}
              {formData.audienceFilters.kyc_status && (
                <div className="flex justify-between">
                  <span style={{color: "var(--color-text-secondary)"}}>KYC Status:</span>
                  <span
                    style={{color: "var(--color-text)"}}
                    className="capitalize"
                  >
                    {formData.audienceFilters.kyc_status}
                  </span>
                </div>
              )}
              {formData.audienceFilters.gender && (
                <div className="flex justify-between">
                  <span style={{color: "var(--color-text-secondary)"}}>Gender:</span>
                  <span style={{color: "var(--color-text)"}}>
                    {formData.audienceFilters.gender === "M" ? "Male" : "Female"}
                  </span>
                </div>
              )}
              {formData.audienceFilters.device && (
                <div className="flex justify-between">
                  <span style={{color: "var(--color-text-secondary)"}}>Device:</span>
                  <span style={{color: "var(--color-text)"}}>
                    {formData.audienceFilters.device === "ios" ? "iOS" : "Android"}
                  </span>
                </div>
              )}
              {formData.audienceFilters.has_wallet && (
                <div className="flex items-center gap-2">
                  <CheckCircle
                    size={14}
                    style={{color: "var(--color-success)"}}
                  />
                  <span
                    style={{color: "var(--color-text)"}}
                    className="text-xs"
                  >
                    Only users with wallets
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Estimated Recipients */}
      <div
        className="p-6 rounded-lg border-2 flex items-center justify-between"
        style={{
          backgroundColor: "var(--color-primary)10",
          borderColor: "var(--color-primary)",
        }}
      >
        <div>
          <p
            className="text-sm font-medium"
            style={{color: "var(--color-text-secondary)"}}
          >
            Estimated Recipients
          </p>
          <p
            className="text-3xl font-bold mt-1"
            style={{color: "var(--color-primary)"}}
          >
            {formatNumber(estimatedRecipients)}
          </p>
        </div>
        <div
          className="text-right text-xs"
          style={{color: "var(--color-text-secondary)"}}
        >
          <p>The campaign will be created as a draft.</p>
          <p>You can send it immediately or schedule it for later.</p>
        </div>
      </div>

      {/* Preview */}
      <div>
        <label
          className="block text-sm font-medium mb-2"
          style={{color: "var(--color-text)"}}
        >
          Preview
        </label>
        <div
          className="p-4 rounded-lg border"
          style={{
            backgroundColor: "var(--color-surface-2)",
            borderColor: "var(--color-surface-2)",
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
              style={{backgroundColor: "var(--color-primary)", color: "white"}}
            >
              <span className="text-sm font-bold">R365</span>
            </div>
            <div className="flex-1">
              <p
                className="text-sm font-semibold mb-1"
                style={{color: "var(--color-text)"}}
              >
                {formData.title}
              </p>
              <p
                className="text-xs"
                style={{color: "var(--color-text-secondary)"}}
              >
                {formData.body}
              </p>
              {formData.imageUrl && (
                <div className="mt-2">
                  <img
                    src={formData.imageUrl}
                    alt="Preview"
                    className="w-full rounded max-h-32 object-cover"
                    onError={e => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
